import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LabelList,
} from 'recharts';
import { Card } from '../ui/card';

const SMALL_N = 10;
const SERIES = [
  { code: '92507', name: '92507 · Treatment', fill: '#685B72' },
  { code: '92609', name: '92609 · AAC', fill: '#B08A3A' },
  { code: '92526', name: '92526 · Feeding', fill: '#8A6FA3' },
];
const nOf = (row, code) => row[`n_${code}`];
const isSmall = (row, code) => { const n = nOf(row, code); return n != null && n < SMALL_N; };
const money = (v) => `$${Math.round(Number(v) || 0).toLocaleString()}`;

const MonthTick = ({ x, y, payload, rows }) => {
  const row = rows.find((r) => r.month === payload.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={14} textAnchor="middle" fontSize={11} fill="#6B6578">{payload.value}</text>
      {row?.maturing && <text dy={28} textAnchor="middle" fontSize={9} fill="#B08A3E">still being paid</text>}
    </g>
  );
};

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-xl border bg-white px-3 py-2 text-xs shadow-sm" data-testid="bi-cpt-trend-tooltip">
      <div className="font-semibold mb-1">{label}{row.maturing ? ' · still being paid' : ''}</div>
      {SERIES.map((s) => {
        const n = nOf(row, s.code);
        if (row[s.code] == null || n === 0) return null;
        return (
          <div key={s.code} className="flex gap-3 justify-between">
            <span className="flex items-center gap-1.5" style={{ color: '#2E2A36' }}>
              <span className="inline-block w-2 h-2 rounded-sm" style={{ background: s.fill }} />{s.name}
            </span>
            <span className="tabular-nums">{money(row[s.code])}{n != null ? ` · ${n} visit${n === 1 ? '' : 's'}${n < SMALL_N ? ' (small sample)' : ''}` : ''}</span>
          </div>
        );
      })}
    </div>
  );
};

export const CptTrendChart = ({ rows, lagDays }) => (
  <Card className="p-5 rounded-2xl mb-8 bg-white border" style={{ borderColor: '#E7E2DA' }} data-testid="bi-cpt-trend-chart">
    <ResponsiveContainer width="100%" height={340}>
      <BarChart data={rows} barCategoryGap="20%" barGap={4}>
        <CartesianGrid strokeDasharray="3 3" stroke="#EFEAE2" vertical={false} />
        <XAxis dataKey="month" tickLine={false} height={40} interval={0} tick={<MonthTick rows={rows} />} />
        <YAxis stroke="#6B6578" fontSize={11} tickLine={false} tickFormatter={money} />
        <Tooltip content={<TrendTooltip />} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {SERIES.map((s) => (
          <Bar key={s.code} dataKey={s.code} fill={s.fill} name={s.name} radius={[4, 4, 0, 0]}>
            {rows.map((r) => {
              const small = isSmall(r, s.code);
              return (
                <Cell key={r.month} fillOpacity={r.maturing ? 0.3 : small ? 0.45 : 1}
                  stroke={small ? s.fill : undefined} strokeDasharray={small ? '3 2' : undefined} />
              );
            })}
            <LabelList dataKey={`n_${s.code}`} position="top" fontSize={9} fill="#8A3A3A"
              formatter={(n) => (n > 0 && n < SMALL_N ? 'small' : '')} />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
    <div className="text-[11px] mt-2" style={{ color: '#6B6578' }} data-testid="bi-cpt-trend-legend-note">
      Faded bars = months less than {lagDays} days old, still being paid · dashed "small" bars = fewer than {SMALL_N} visits · months with no visits for a code show no bar
    </div>
  </Card>
);

export default CptTrendChart;
